import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SearchBar from './components/SearchBar';
import CategoryFilter from './components/CategoryFilter';
import ArticleCard from './components/ArticleCard';
import TrendingSection from './components/TrendingSection';
import FeaturedSection from './components/FeaturedSection';

const GiftIdeasHub = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState('newest');
  const navigate = useNavigate();

  // Mock articles data optimized for SEO and GEO
  const mockArticles = [
    {
      id: 1,
      title: "Gift Ideas for 16-Year-Old Gaming Enthusiasts: Ultimate Guide 2024",
      slug: "gift-ideas-16-year-old-gaming-enthusiasts-2024",
      excerpt: "Discover the perfect gifts for teenage gamers with our comprehensive guide featuring the latest gaming accessories, tech gadgets, and unique items that will level up their gaming experience.",
      content: "Full article content with expert recommendations...",
      author: {
        name: "Sarah Johnson",
        avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
        credentials: "Gaming Industry Specialist, 8+ years",
        isVerified: true
      },
      category: "Age Groups",
      subcategory: "Teens",
      tags: ["16-year-old", "gaming", "teenagers", "tech", "accessories", "2024"],
      image: "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-15'),
      readTime: 8,
      views: 12847,
      shares: 342,
      likes: 1156,
      difficulty: "Easy",
      budgetRange: "$25 - $200",
      occasionTags: ["birthday", "holiday", "just-because"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "Dr. Emily Chen, Child Development Specialist",
          quote: "Gaming can enhance problem-solving skills and creativity in teenagers when balanced appropriately."
        }
      ],
      citations: [
        "Gaming Industry Report 2024 - TechStats",
        "Teenage Gift Preferences Study - Consumer Insights"
      ]
    },
    {
      id: 2,
      title: "Thoughtful Birthday Gifts for Mom Who Has Everything: 25 Unique Ideas",
      slug: "thoughtful-birthday-gifts-mom-who-has-everything-2024",
      excerpt: "Find the perfect birthday gift for your mom with our curated collection of unique, meaningful presents that show how much you care. From personalized keepsakes to experience gifts.",
      content: "Full article content with heartfelt recommendations...",
      author: {
        name: "Michael Thompson",
        avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
        credentials: "Family Gift Curator, 10+ years",
        isVerified: true
      },
      category: "Relationships",
      subcategory: "Parents",
      tags: ["mom", "birthday", "unique", "thoughtful", "personalized", "experiences"],
      image: "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-12'),
      readTime: 12,
      views: 18392,
      shares: 567,
      likes: 2043,
      difficulty: "Medium",
      budgetRange: "$30 - $500",
      occasionTags: ["birthday", "mothers-day", "holiday"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "Maria Rodriguez, Family Therapist",
          quote: "The most meaningful gifts often reflect shared memories and personal connections rather than monetary value."
        }
      ],
      citations: [
        "Mother's Day Gift Trends 2024 - Gift Industry Association",
        "Emotional Impact of Gift-Giving - Psychology Today"
      ]
    },
    {
      id: 3,
      title: "Budget-Friendly Holiday Gifts Under $50: 40 Ideas That Don't Look Cheap",
      slug: "budget-friendly-holiday-gifts-under-50-2024",
      excerpt: "Celebrate the holidays without breaking the bank. Our expert-curated list of affordable gifts proves that thoughtfulness doesn't require a big budget.",
      content: "Full article content with budget-conscious recommendations...",
      author: {
        name: "Jessica Kim",
        avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
        credentials: "Budget Lifestyle Expert, 6+ years",
        isVerified: true
      },
      category: "Budget Ranges",
      subcategory: "Under $50",
      tags: ["budget", "holiday", "affordable", "cheap", "under-50", "value"],
      image: "https://images.unsplash.com/photo-1512389142860-9c449e58a543?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-10'),
      readTime: 10,
      views: 25186,
      shares: 891,
      likes: 3247,
      difficulty: "Easy",
      budgetRange: "Under $50",
      occasionTags: ["holiday", "christmas", "birthday", "just-because"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "David Park, Consumer Finance Advisor",
          quote: "Smart gifting is about finding the intersection of meaningful and affordable - it's absolutely possible with the right approach."
        }
      ],
      citations: [
        "Holiday Spending Trends 2024 - National Retail Federation",
        "Budget Gift Guide Survey - Consumer Reports"
      ]
    },
    {
      id: 4,
      title: "Romantic Anniversary Gifts for Him: 30 Ideas to Celebrate Your Love",
      slug: "romantic-anniversary-gifts-for-him-2024",
      excerpt: "Make your anniversary unforgettable with romantic gifts that express your love and appreciation. From personalized keepsakes to adventure experiences.",
      content: "Full article content with romantic recommendations...",
      author: {
        name: "Emma Rodriguez",
        avatar: "https://images.unsplash.com/photo-1517841905240-472988babdf9?w=150&h=150&fit=crop&crop=face",
        credentials: "Relationship Gift Specialist, 7+ years",
        isVerified: true
      },
      category: "Occasions",
      subcategory: "Anniversary",
      tags: ["anniversary", "romantic", "him", "boyfriend", "husband", "love"],
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-08'),
      readTime: 9,
      views: 14629,
      shares: 423,
      likes: 1876,
      difficulty: "Medium",
      budgetRange: "$40 - $300",
      occasionTags: ["anniversary", "valentine\'s", "birthday"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "Dr. Amanda Foster, Relationship Counselor",
          quote: "The best romantic gifts often reflect your partner's interests while celebrating your unique relationship journey."
        }
      ],
      citations: [
        "Anniversary Gift Trends 2024 - Love & Relationships Magazine",
        "Psychology of Gift-Giving in Relationships - Journal of Social Psychology"
      ]
    },
    {
      id: 5,
      title: "Creative DIY Gifts for Best Friends: 15 Handmade Ideas They'll Treasure",
      slug: "creative-diy-gifts-best-friends-handmade-2024",
      excerpt: "Show your best friend how much they mean to you with these heartfelt DIY gift ideas. Easy tutorials and meaningful projects that celebrate your friendship.",
      content: "Full article content with DIY tutorials...",
      author: {
        name: "Alex Chen",
        avatar: "https://images.unsplash.com/photo-1519345182560-3f2917c472ef?w=150&h=150&fit=crop&crop=face",
        credentials: "DIY Craft Expert, 5+ years",
        isVerified: true
      },
      category: "Interests & Hobbies",
      subcategory: "DIY & Crafts",
      tags: ["diy", "handmade", "best-friend", "creative", "crafts", "friendship"],
      image: "https://images.unsplash.com/photo-1606107557195-0e29a4b5b4aa?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-05'),
      readTime: 11,
      views: 9876,
      shares: 298,
      likes: 1432,
      difficulty: "Medium",
      budgetRange: "$10 - $75",
      occasionTags: ["friendship", "birthday", "just-because"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "Lisa Park, Craft Therapist",
          quote: "Handmade gifts carry emotional weight that store-bought items simply can't match - they represent time, effort, and love."
        }
      ],
      citations: [
        "DIY Gift Trends 2024 - Craft Industry Report",
        "Friendship and Gift-Giving Study - Social Psychology Review"
      ]
    },
    {
      id: 6,
      title: "Luxury Gifts for Her: Splurge-Worthy Presents She'll Love Forever",
      slug: "luxury-gifts-for-her-splurge-worthy-2024",
      excerpt: "When you want to make a grand gesture, these luxury gifts for her will leave a lasting impression. From designer accessories to exclusive experiences.",
      content: "Full article content with luxury recommendations...",
      author: {
        name: "Victoria Sterling",
        avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
        credentials: "Luxury Lifestyle Consultant, 12+ years",
        isVerified: true
      },
      category: "Budget Ranges",
      subcategory: "Luxury",
      tags: ["luxury", "her", "expensive", "designer", "premium", "high-end"],
      image: "https://images.unsplash.com/photo-1583292650898-7d22cd27ca6f?w=600&h=400&fit=crop",
      publishedAt: new Date('2024-01-03'),
      readTime: 7,
      views: 8234,
      shares: 187,
      likes: 967,
      difficulty: "Easy",
      budgetRange: "$500+",
      occasionTags: ["anniversary", "birthday", "holiday"],
      geoOptimized: true,
      expertQuotes: [
        {
          expert: "Caroline Mitchell, Luxury Brand Specialist",
          quote: "Luxury gifts are about creating moments of joy and lasting memories - the price reflects the experience and craftsmanship."
        }
      ],
      citations: [
        "Luxury Gift Market Report 2024 - Premium Brands Institute",
        "High-End Consumer Behavior Study - Luxury Research Group"
      ]
    }
  ];

  const categories = [
    { name: 'All', count: 156, icon: 'Grid' },
    { name: 'Age Groups', count: 42, icon: 'Users' },
    { name: 'Interests & Hobbies', count: 38, icon: 'Heart' },
    { name: 'Occasions', count: 29, icon: 'Calendar' },
    { name: 'Relationships', count: 31, icon: 'UserHeart' },
    { name: 'Budget Ranges', count: 16, icon: 'DollarSign' }
  ];

  const sortOptions = [
    { value: 'newest', label: 'Newest First' },
    { value: 'popular', label: 'Most Popular' },
    { value: 'trending', label: 'Trending' },
    { value: 'read-time', label: 'Quick Reads' }
  ];

  useEffect(() => {
    setArticles(mockArticles);
    setFilteredArticles(mockArticles);
  }, []);

  useEffect(() => {
    let filtered = articles;

    // Filter by category
    if (activeCategory !== 'All') {
      filtered = filtered.filter(article => article.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article => 
        article.title.toLowerCase().includes(query) ||
        article.excerpt.toLowerCase().includes(query) ||
        article.tags.some(tag => tag.toLowerCase().includes(query)) ||
        article.author.name.toLowerCase().includes(query)
      );
    }

    // Sort articles
    switch (sortBy) {
      case 'popular':
        filtered.sort((a, b) => b.views - a.views);
        break;
      case 'trending':
        filtered.sort((a, b) => (b.views + b.shares * 10) - (a.views + a.shares * 10));
        break;
      case 'read-time':
        filtered.sort((a, b) => a.readTime - b.readTime);
        break;
      default:
        filtered.sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt));
    }

    setFilteredArticles(filtered);
  }, [articles, activeCategory, searchQuery, sortBy]);

  const handleArticleClick = (article) => {
    navigate(`/article-detail-page?slug=${article.slug}`);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  const handleSortChange = (sort) => {
    setSortBy(sort);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Giftsy Modern Gift Ideas Hub",
    "description": "Comprehensive gift guides and ideas for every occasion, relationship, and budget",
    "url": "https://giftsy.com/gift-ideas-hub",
    "publisher": {
      "@type": "Organization",
      "name": "Giftsy Modern",
      "logo": {
        "@type": "ImageObject",
        "url": "https://giftsy.com/assets/images/Giftsy-Logo-Final-1751671984839.png"
      }
    },
    "mainEntity": filteredArticles.map(article => ({
      "@type": "BlogPosting",
      "headline": article.title,
      "description": article.excerpt,
      "url": `https://giftsy.com/article-detail-page?slug=${article.slug}`,
      "datePublished": article.publishedAt.toISOString(),
      "author": {
        "@type": "Person",
        "name": article.author.name
      },
      "image": article.image
    }))
  };

  return (
    <>
      <Helmet>
        <title>Gift Ideas Hub - Expert Gift Guides & Ideas | Giftsy Modern</title>
        <meta name="description" content="Discover the perfect gift with our comprehensive gift guides. Expert-curated ideas for every age, occasion, relationship, and budget. Updated daily with trending gift recommendations." />
        <meta name="keywords" content="gift ideas, gift guides, birthday gifts, holiday gifts, anniversary gifts, gift recommendations, thoughtful gifts, unique gifts" />
        <meta property="og:title" content="Gift Ideas Hub - Expert Gift Guides & Ideas | Giftsy Modern" />
        <meta property="og:description" content="Discover the perfect gift with our comprehensive gift guides. Expert-curated ideas for every age, occasion, relationship, and budget." />
        <meta property="og:image" content="https://giftsy.com/assets/images/gift-ideas-hub-og.jpg" />
        <meta property="og:url" content="https://giftsy.com/gift-ideas-hub" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Gift Ideas Hub - Expert Gift Guides & Ideas | Giftsy Modern" />
        <meta name="twitter:description" content="Discover the perfect gift with our comprehensive gift guides. Expert-curated ideas for every age, occasion, relationship, and budget." />
        <meta name="twitter:image" content="https://giftsy.com/assets/images/gift-ideas-hub-twitter.jpg" />
        <link rel="canonical" href="https://giftsy.com/gift-ideas-hub" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-surface">
        <Header />
        
        <div className="pt-20 pb-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-12">
              <h1 className="font-heading font-bold text-4xl lg:text-5xl text-accent-800 mb-6">
                Gift Ideas Hub
              </h1>
              <p className="text-xl text-accent-600 max-w-3xl mx-auto mb-8">
                Discover thoughtful gift ideas backed by expert research and real experiences. 
                Our comprehensive guides help you find the perfect present for every person, 
                occasion, and budget.
              </p>
              
              {/* Key Stats */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary mb-1">150+</div>
                  <div className="text-sm text-accent-600">Expert Guides</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-secondary mb-1">94%</div>
                  <div className="text-sm text-accent-600">Success Rate</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-accent-600 mb-1">1M+</div>
                  <div className="text-sm text-accent-600">Happy Gifters</div>
                </div>
              </div>
            </div>

            {/* Featured Section */}
            <FeaturedSection articles={mockArticles.slice(0, 3)} onArticleClick={handleArticleClick} />

            {/* Trending Section */}
            <TrendingSection articles={mockArticles.slice(3, 6)} onArticleClick={handleArticleClick} />

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mt-12">
              {/* Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                {/* Search */}
                <SearchBar onSearch={handleSearch} />
                
                {/* Categories */}
                <CategoryFilter 
                  categories={categories}
                  activeCategory={activeCategory}
                  onCategoryChange={handleCategoryChange}
                />

                {/* Newsletter Signup */}
                <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 border border-primary-100">
                  <div className="text-center">
                    <Icon name="Mail" size={32} className="text-primary mx-auto mb-4" />
                    <h3 className="font-heading font-semibold text-lg text-accent-800 mb-2">
                      Weekly Gift Ideas
                    </h3>
                    <p className="text-sm text-accent-600 mb-4">
                      Get curated gift recommendations delivered to your inbox
                    </p>
                    <Button variant="primary" size="sm" fullWidth>
                      Subscribe Now
                    </Button>
                  </div>
                </div>

                {/* Popular Tags */}
                <div className="bg-white rounded-2xl shadow-soft border border-accent-100 p-6">
                  <h3 className="font-heading font-semibold text-lg text-accent-800 mb-4">
                    Popular Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['birthday', 'holiday', 'anniversary', 'mom', 'dad', 'teenager', 'budget', 'luxury', 'diy', 'unique'].map(tag => (
                      <button
                        key={tag}
                        onClick={() => setSearchQuery(tag)}
                        className="px-3 py-1 text-sm bg-accent-100 text-accent-700 rounded-full hover:bg-accent-200 transition-colors"
                      >
                        #{tag}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Articles Grid */}
              <div className="lg:col-span-3">
                {/* Sort Controls */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                  <div>
                    <h2 className="font-heading font-semibold text-2xl text-accent-800">
                      {activeCategory === 'All' ? 'All Articles' : `${activeCategory} Articles`}
                    </h2>
                    <p className="text-accent-600 mt-1">
                      {filteredArticles.length} articles found
                    </p>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-accent-600">Sort by:</span>
                    <select
                      value={sortBy}
                      onChange={(e) => handleSortChange(e.target.value)}
                      className="bg-white border border-accent-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                      {sortOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Articles Grid */}
                {filteredArticles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {filteredArticles.map((article) => (
                      <ArticleCard
                        key={article.id}
                        article={article}
                        onClick={() => handleArticleClick(article)}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <Icon name="Search" size={48} className="text-accent-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg text-accent-600 mb-2">
                      No articles found
                    </h3>
                    <p className="text-accent-500 mb-6">
                      Try adjusting your search or browse different categories
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery('');
                        setActiveCategory('All');
                      }}
                    >
                      View All Articles
                    </Button>
                  </div>
                )}

                {/* Load More */}
                {filteredArticles.length > 0 && (
                  <div className="text-center mt-12">
                    <Button
                      variant="ghost"
                      iconName="ChevronDown"
                      iconPosition="right"
                      size="lg"
                    >
                      Load More Articles
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GiftIdeasHub;